import moment from 'moment';

export const getFormElement = (formJson, fieldName) => {
  return formJson.find((element) => element.field_name === fieldName);
};

export const getOptionText = (element, selectedValues) => {
  if (!element || !element.options) return selectedValues;

  if (Array.isArray(selectedValues)) {
    return selectedValues.map((value) => {
      const option = element.options.find((opt) => opt.value === value || opt.key === value);
      return option ? option.text : value;
    });
  } else {
    const option = element.options.find(
      (opt) => opt.value === selectedValues || opt.key === selectedValues
    );
    return option ? option.text : selectedValues;
  }
};

export const getElementLabel = (formJson, fieldName) => {
  const element = getFormElement(formJson, fieldName);
  const rawLabel = element ? element.label || element.text : '';
  return parseHtmlContent(rawLabel);
};

export const formatDate = (dateString) => {
  if (!dateString) return '';
  return moment(dateString).format('MM/DD/YYYY');
};

export const createSectionTitle = (title) => {
  return {
    text: title,
    style: 'sectionHeader',
    margin: [0, 12, 0, 6]
  };
};

export const createSummaryTitle = () => {
  return {
    text: 'SUMMARY',
    style: 'summaryHeader',
    margin: [0, 10, 0, 3]
  };
};

export const createTable = (headers, rows, widths = null) => {
  return {
    table: {
      headerRows: 1,
      widths: widths || headers.map(() => '*'),
      body: [headers.map((header) => ({ text: header, style: 'tableHeader' })), ...rows]
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: () => 0,
      paddingRight: () => 10,
      paddingTop: () => 4,
      paddingBottom: () => 4
    },
    margin: [0, 6, 0, 0]
  };
};

export const createFormFieldHeader = (type, label) => {
  // Don't show header at all if there's no meaningful label
  if (!label) {
    return null;
  }

  // Handle string labels
  if (typeof label === 'string') {
    const trimmedLabel = label.trim();
    if (trimmedLabel === '' || trimmedLabel === 'Field') {
      return null;
    }
  }

  // Handle array labels (from parseHtmlContent for formatted text)
  if (Array.isArray(label)) {
    if (label.length === 0) {
      return null;
    }
    // Check if array contains only empty strings
    const hasContent = label.some((item) => {
      if (typeof item === 'string') {
        return item.trim() !== '';
      }
      if (typeof item === 'object' && item.text) {
        return item.text.trim() !== '';
      }
      return true; // If it's some other object, assume it has content
    });
    if (!hasContent) {
      return null;
    }
  }

  return {
    table: {
      widths: ['70%', '30%'],
      body: [
        [
          {
            text: label,
            style: 'fieldLabel',
            border: [false, false, false, false],
            alignment: 'left'
          },
          {
            text: type,
            style: 'fieldType',
            border: [false, false, false, false],
            alignment: 'right'
          }
        ]
      ]
    },
    layout: 'noBorders',
    margin: [0, 20, 0, 4]
  };
};

export const getCheckedCheckboxSVG = () => {
  return `<svg width="12" height="12" viewBox="0 0 12 12">
    <rect width="12" height="12" fill="#0D3B50" stroke="#0D3B50" stroke-width="1" rx="1"/>
    <path d="M3 6l2 2 4-4" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
};

export const getUncheckedCheckboxSVG = () => {
  return `<svg width="12" height="12" viewBox="0 0 12 12">
    <rect width="12" height="12" fill="white" stroke="#ccc" stroke-width="1" rx="1"/>
  </svg>`;
};

export const getSelectedRadioSVG = () => {
  return `<svg width="12" height="12" viewBox="0 0 12 12">
    <circle cx="6" cy="6" r="5" fill="white" stroke="#0D3B50" stroke-width="1.5"/>
    <circle cx="6" cy="6" r="2.5" fill="#0D3B50"/>
  </svg>`;
};

export const getUnselectedRadioSVG = () => {
  return `<svg width="12" height="12" viewBox="0 0 12 12">
    <circle cx="6" cy="6" r="5" fill="white" stroke="#ccc" stroke-width="1.5"/>
  </svg>`;
};

export const getFilledStarSVG = () => {
  return `<svg width="16" height="16" viewBox="0 0 194 185" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M88.7797 5.44152C91.9509 -1.67287 102.049 -1.67286 105.22 5.44153L126.38 52.9116C127.688 55.8459 130.461 57.8605 133.656 58.1977L185.341 63.6528C193.087 64.4704 196.208 74.0743 190.422 79.2888L151.814 114.082C149.427 116.233 148.368 119.492 149.035 122.635L159.818 173.477C161.434 181.096 153.265 187.032 146.517 183.14L101.497 157.174C98.7137 155.568 95.2863 155.568 92.5034 157.174L47.4825 183.14C40.7352 187.032 32.5656 181.096 34.1818 173.477L44.9653 122.635C45.6319 119.492 44.5728 116.233 42.1863 114.082L3.57827 79.2888C-2.20795 74.0743 0.912556 64.4704 8.65871 63.6528L60.3442 58.1977C63.5391 57.8605 66.3119 55.8459 67.6199 52.9116L88.7797 5.44152Z" fill="#0D3B50"/>
  </svg>`;
};

export const getEmptyStarSVG = () => {
  return `<svg width="16" height="16" viewBox="0 0 194 185" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M88.7797 5.44152C91.9509 -1.67287 102.049 -1.67286 105.22 5.44153L126.38 52.9116C127.688 55.8459 130.461 57.8605 133.656 58.1977L185.341 63.6528C193.087 64.4704 196.208 74.0743 190.422 79.2888L151.814 114.082C149.427 116.233 148.368 119.492 149.035 122.635L159.818 173.477C161.434 181.096 153.265 187.032 146.517 183.14L101.497 157.174C98.7137 155.568 95.2863 155.568 92.5034 157.174L47.4825 183.14C40.7352 187.032 32.5656 181.096 34.1818 173.477L44.9653 122.635C45.6319 119.492 44.5728 116.233 42.1863 114.082L3.57827 79.2888C-2.20795 74.0743 0.912556 64.4704 8.65871 63.6528L60.3442 58.1977C63.5391 57.8605 66.3119 55.8459 67.6199 52.9116L88.7797 5.44152Z" fill="white" stroke="#E5E7EB" stroke-width="1"/>
  </svg>`;
};

export const getHalfStarSVG = () => {
  return `<svg width="16" height="16" viewBox="0 0 194 185" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="halfStarClip">
        <rect x="0" y="0" width="97" height="185"/>
      </clipPath>
    </defs>
    <path d="M88.7797 5.44152C91.9509 -1.67287 102.049 -1.67286 105.22 5.44153L126.38 52.9116C127.688 55.8459 130.461 57.8605 133.656 58.1977L185.341 63.6528C193.087 64.4704 196.208 74.0743 190.422 79.2888L151.814 114.082C149.427 116.233 148.368 119.492 149.035 122.635L159.818 173.477C161.434 181.096 153.265 187.032 146.517 183.14L101.497 157.174C98.7137 155.568 95.2863 155.568 92.5034 157.174L47.4825 183.14C40.7352 187.032 32.5656 181.096 34.1818 173.477L44.9653 122.635C45.6319 119.492 44.5728 116.233 42.1863 114.082L3.57827 79.2888C-2.20795 74.0743 0.912556 64.4704 8.65871 63.6528L60.3442 58.1977C63.5391 57.8605 66.3119 55.8459 67.6199 52.9116L88.7797 5.44152Z" fill="white" stroke="#E5E7EB" stroke-width="1"/>
    <path d="M88.7797 5.44152C91.9509 -1.67287 102.049 -1.67286 105.22 5.44153L126.38 52.9116C127.688 55.8459 130.461 57.8605 133.656 58.1977L185.341 63.6528C193.087 64.4704 196.208 74.0743 190.422 79.2888L151.814 114.082C149.427 116.233 148.368 119.492 149.035 122.635L159.818 173.477C161.434 181.096 153.265 187.032 146.517 183.14L101.497 157.174C98.7137 155.568 95.2863 155.568 92.5034 157.174L47.4825 183.14C40.7352 187.032 32.5656 181.096 34.1818 173.477L44.9653 122.635C45.6319 119.492 44.5728 116.233 42.1863 114.082L3.57827 79.2888C-2.20795 74.0743 0.912556 64.4704 8.65871 63.6528L60.3442 58.1977C63.5391 57.8605 66.3119 55.8459 67.6199 52.9116L88.7797 5.44152Z" fill="#0D3B50" clip-path="url(#halfStarClip)"/>
  </svg>`;
};

export const parseHtmlContent = (htmlString) => {
  if (!htmlString) return '';

  // If it's plain text without HTML tags, return as string
  if (!htmlString.includes('<')) return htmlString;

  let html = htmlString;

  // Replace block elements with line breaks
  html = html.replace(/<p[^>]*>/gi, '');
  html = html.replace(/<\/p>/gi, '\n');
  html = html.replace(/<div[^>]*>/gi, '');
  html = html.replace(/<\/div>/gi, '\n');
  html = html.replace(/<br[^>]*>/gi, '\n');
  html = html.replace(/<h[1-6][^>]*>/gi, '');
  html = html.replace(/<\/h[1-6]>/gi, '\n');

  // Clean up multiple line breaks
  html = html.replace(/\n\s*\n/g, '\n');
  html = html.trim();

  // If no formatting tags remain, return as string
  if (!html.match(/<(strong|b|em|i|u)\b[^>]*>/i)) {
    return html.replace(/<[^>]*>/g, '');
  }

  // Parse formatted content into pdfMake rich text format
  const result = [];
  let currentText = '';
  let position = 0;

  const tagStack = [];
  const tagRegex = /<\/?(?:strong|b|em|i|u)\b[^>]*>/gi;

  let match;
  while ((match = tagRegex.exec(html)) !== null) {
    // Add text before this tag
    if (match.index > position) {
      currentText += html.substring(position, match.index);
    }

    const tag = match[0];
    const isClosing = tag.startsWith('</');
    const tagName = tag.match(/<\/?(\w+)/)[1].toLowerCase();

    if (!isClosing) {
      // Opening tag - push current text and start new formatted section
      if (currentText) {
        result.push(getTextWithFormatting(currentText, tagStack));
        currentText = '';
      }
      tagStack.push(tagName);
    } else {
      // Closing tag - push formatted text and remove from stack
      if (currentText) {
        result.push(getTextWithFormatting(currentText, tagStack));
        currentText = '';
      }
      const index = tagStack.lastIndexOf(tagName);
      if (index > -1) {
        tagStack.splice(index, 1);
      }
    }

    position = match.index + match[0].length;
  }

  // Add remaining text
  if (position < html.length) {
    currentText += html.substring(position);
  }
  if (currentText) {
    result.push(getTextWithFormatting(currentText, tagStack));
  }

  // If we only have one text element with no formatting, return as string
  if (result.length === 1 && typeof result[0] === 'string') {
    return result[0];
  }

  return result.length > 0 ? result : html.replace(/<[^>]*>/g, '');
};

const getTextWithFormatting = (text, tagStack) => {
  // Remove any remaining HTML tags
  const cleanText = text.replace(/<[^>]*>/g, '');

  if (!cleanText) return '';

  if (tagStack.length === 0) {
    return cleanText;
  }

  const formatting = {
    text: cleanText
  };

  tagStack.forEach((tag) => {
    switch (tag) {
      case 'strong':
      case 'b':
        formatting.bold = true;
        break;
      case 'em':
      case 'i':
        formatting.italics = true;
        break;
      case 'u':
        formatting.decoration = 'underline';
        break;
    }
  });

  return formatting;
};
